<template>
	<div
		class="announcement-view"
		:class="{
			scheduled: announcement.isScheduled,
		}"
	>
		<div class="announcement-view-header">
			<div class="target-label" v-t="'announcements_new_recipienttitle'" />
			<AnnouncementTargets
				class="targets"
				:announcement="announcement"
				showSentTo
			/>
			<Button
				small
				class="seenby-btn"
				v-if="canViewSeenBy"
				@click="showReceipts = true"
			>
				<EyeIcon type="regular" />
				{{ $t('readreceipts_seen_by_action') }}
			</Button>
			<DotsMenu
				v-if="showMenu"
				class="dots-menu"
				:items="menuItems"
				localize
				@click.stop
				@select="onMenuItemClick"
			/>
		</div>
		<div class="announcement-sender-container">
			<AnnouncementSender
				:announcement="announcement"
				showProfile
				showTranslationTip
				:showDate="!announcement.isScheduled"
				profileSize="36px"
			/>
		</div>
		<div
			class="announcement-text"
			:class="{ translated: announcement.isTranslated }"
			data-private
			v-safe-html="textWithLinks"
		/>
		<div class="announcement-media" v-if="announcement.hasMedia">
			<MediaView :media="announcement.media" :key="announcement.media.id" />
		</div>

		<AnnouncementDeleteModal
			v-if="showDelete"
			:announcementId="announcement.id"
			:forEveryone="forEveryone"
			@cancel="showDelete = false"
		/>

		<ReadReceiptsModal
			v-if="showReceipts"
			:receiptsUrl="`announcements/${announcement.id}/receipts`"
			:countsUrl="`announcements/${announcement.id}/receipts/counts`"
			@cancel="showReceipts = false"
		/>
	</div>
</template>

<script>
import { getMyUser } from '@/store'
import { Button, DotsMenu } from '@/components/common'
import { EyeIcon } from '@/icons'
import AnnouncementTargets from './AnnouncementTargets'
import AnnouncementSender from './AnnouncementSender'
import MediaView from '@/components/MediaView'
import autolinker from 'autolinker'
import AnnouncementDeleteModal from './AnnouncementDeleteModal'
import ReadReceiptsModal from '@/components/Message/ReadReceiptsModal'
import { orgCan, DELETE_ANY_ANNOUNCEMENT } from '@/helpers/permissions'

export default {
	name: 'AnnouncementView',
	components: {
		Button,
		EyeIcon,
		AnnouncementTargets,
		DotsMenu,
		AnnouncementSender,
		AnnouncementDeleteModal,
		MediaView,
		ReadReceiptsModal,
	},
	props: {
		announcement: Object,
		showActions: { type: Boolean, default: null },
	},
	data() {
		return {
			showSentTo: false,
			showDelete: false,
			forEveryone: false,
			showReceipts: false,
		}
	},
	computed: {
		menuItems() {
			const { isScheduled, isSentByMe } = this.announcement
			if (isSentByMe || orgCan(DELETE_ANY_ANNOUNCEMENT)) {
				if (isScheduled) {
					return [
						{
							id: 'delete_everyone',
							label: 'common_button_delete',
							danger: true,
						},
					]
				}
				return [
					{
						id: 'delete',
						label: 'announcements_sent_confirmdelete_me_button',
					},
					{
						id: 'delete_everyone',
						label: 'announcements_sent_confirmdelete_button',
						danger: true,
					},
				]
			}
			return [
				{
					id: 'delete',
					label: 'common_button_delete',
					danger: true,
				},
			]
		},
		canViewSeenBy() {
			if (!this.showActions || !this.announcement.isSent) return false
			const me = getMyUser()
			return me.isAdmin || me.id === this.announcement.senderuser_id
		},
		showMenu() {
			return this.showActions && this.menuItems.length > 0
		},
		textWithLinks() {
			return autolinker.link(this.announcement.text, {
				className: 'linkified',
				phone: false,
				stripPrefix: false,
				decodePercentEncoding: false,
			})
		},
	},
	methods: {
		onMenuItemClick({ id }) {
			if (id === 'delete_everyone') {
				this.deleteForEveryone()
			} else {
				this.delete()
			}
		},
		delete() {
			this.forEveryone = false
			this.showDelete = true
		},
		deleteForEveryone() {
			this.forEveryone = true
			this.showDelete = true
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.announcement-view {
	@include flex-column;
	padding: 0 16px;
	max-height: 100%;

	.announcement-view-header {
		@include flex(12px);
		height: 52px;
		flex-shrink: 0;
		border-bottom: 1px solid var(--neutral-200);

		.targets {
			flex-grow: 1;
			overflow: hidden;
		}
		.seenby-btn {
			flex-shrink: 0;
			@include flex(6px);
			padding: 0 12px !important;
		}
		.dots-menu {
			flex-shrink: 0;
			color: var(--neutral-400);
		}
	}
	.announcement-sender-container {
		flex-shrink: 0;
		border-bottom: 1px solid var(--neutral-200);
		:deep(.announcement-sender) {
			.announcement-sender-name {
				font-size: 15px;
			}
		}
	}
	.announcement-text {
		margin: 16px 0;
		padding: 0;
		flex-grow: 1;
		overflow-x: hidden;
		overflow-y: auto;
		white-space: pre-wrap;
		min-height: 80px;

		&.translated {
			margin: 13px -3px;
			padding: 3px;
			background: var(--blue-100);
			border-radius: 8px;
		}
	}
	.announcement-media {
		cursor: pointer;
		padding-top: 16px;
		border-top: 1px solid var(--neutral-200);

		:deep(.photo-video) {
			max-width: 300px !important;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.announcement-view {
		.announcement-text {
			&.translated {
				background: rgba(73, 164, 218, 0.15) !important;
			}
		}
	}
}
</style>
